import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import moment, { Moment } from 'moment';
import {
  EsbPaService,
  BenOption,
  OccClass,
  TIER_NAMES,
} from 'src/app/services/esb-pa.service';

@Component({
  selector: 'app-pa-tier-breakdown',
  templateUrl: './pa-tier-breakdown.component.html',
  styleUrls: ['./pa-tier-breakdown.component.css']
})
export class PaTierBreakdownComponent implements OnInit, OnDestroy {

  readonly OTHER_OCCUPATION = 'Other';

  token: string = '';
  coverType: string = '';
  isRenewal: boolean = false;
  pageHeading: string = 'Select Cover';
  tierSelectLabel: string = 'Select Cover';
  saveContinue: string = 'Save and Continue';

  // ── Loading / error flags ─────────────────────────────────────────────────
  //
  // A single isLoadingBenefit flag covers the whole pre-fetch batch (all four
  // ESB calls fired in parallel when tier + occupation are both selected).
  // isLoadingExcelsior / isLoadingPassiveWar mirror it so the HTML spinners
  // inside the excelsior card and passive-war row still work.
  // One error path covers all — ESB is the single source of truth.

  isLoadingBenefit: boolean = false;
  benefitLoadError: string | null = null;

  isLoadingExcelsior: boolean = false;
  excelsiorLoadError: string | null = null;   // mirrors benefitLoadError

  isLoadingPassiveWar: boolean = false;
  passiveWarLoadError: string | null = null;  // mirrors benefitLoadError

  isSubmitting: boolean = false;
  coverBenefitsHidden: boolean = false;
  excelsiorBenefitsHidden: boolean = false;

  // ── Pre-fetched ESB results ───────────────────────────────────────────────
  //
  // All four combinations are fetched at once when the user has chosen both
  // a tier and an occupation.  Checkbox toggles simply show/hide the values
  // that are already in memory — no additional ESB call is needed.
  //
  // Both benefit limits AND premiums are class-specific, so nothing is
  // displayed until occupation is known.

  // All 6 class codes fetched upfront when tier is selected.
  // Getters below read the right entry for the currently selected occupation class.
  private _std: Partial<Record<OccClass, { premium: number; premiumWithWar: number; rawBenefits: { BenefitDescription: string; BenefitLimit: number; AnnualPremium?: number; TLevy?: number; Pcf?: number; StampDuty?: number }[] }>> = {};
  private _exc: Partial<Record<OccClass, { premium: number; premiumWithWar: number; rawBenefits: { BenefitDescription: string; BenefitLimit: number; AnnualPremium?: number; TLevy?: number; Pcf?: number; StampDuty?: number }[] }>> = {};

  get currentPremium(): number | null              { const c = this.getOccClass(); return c ? (this._std[c]?.premium            ?? null) : null; }
  get currentPremiumWithWar(): number | null        { const c = this.getOccClass(); return c ? (this._std[c]?.premiumWithWar      ?? null) : null; }
  get currentRawBenefits(): { BenefitDescription: string; BenefitLimit: number; AnnualPremium?: number; TLevy?: number; Pcf?: number; StampDuty?: number }[]          { const c = this.getOccClass(); return c ? (this._std[c]?.rawBenefits ?? []) : []; }
  get currentExcelsiorPremium(): number | null      { const c = this.getOccClass(); return c ? (this._exc[c]?.premium            ?? null) : null; }
  get currentExcelsiorPremiumWithWar(): number | null { const c = this.getOccClass(); return c ? (this._exc[c]?.premiumWithWar    ?? null) : null; }
  get currentExcelsiorRawBenefits(): { BenefitDescription: string; BenefitLimit: number; AnnualPremium?: number; TLevy?: number; Pcf?: number; StampDuty?: number }[] { const c = this.getOccClass(); return c ? (this._exc[c]?.rawBenefits ?? []) : []; }

  // ── Stale-request guard ───────────────────────────────────────────────────
  // A single ID covers the whole four-call batch.  Stale batches are discarded.
  private _fetchId = 0;

  // ── Occupations ───────────────────────────────────────────────────────────
  occupations: { class: string; label: string }[] = [
    { class: 'CLASS I (Low Risk)', label: 'CEO / Managing Director' },
    { class: 'CLASS I (Low Risk)', label: 'General Manager' },
    { class: 'CLASS I (Low Risk)', label: 'Human Resource Manager' },
    { class: 'CLASS I (Low Risk)', label: 'Company Secretary' },
    { class: 'CLASS I (Low Risk)', label: 'Accountant' },
    { class: 'CLASS I (Low Risk)', label: 'Office Supervisor' },
    { class: 'CLASS I (Low Risk)', label: 'Office Manager' },
    { class: 'CLASS I (Low Risk)', label: 'Office Clerk' },
    { class: 'CLASS I (Low Risk)', label: 'Nurse' },
    { class: 'CLASS I (Low Risk)', label: 'Student' },
    { class: 'CLASS I (Low Risk)', label: 'Doctor' },
    { class: 'CLASS I (Low Risk)', label: 'Lawyer' },
    { class: 'CLASS I (Low Risk)', label: 'Architect' },
    { class: 'CLASS I (Low Risk)', label: 'Bookseller' },
    { class: 'CLASS I (Low Risk)', label: 'Chemist (Dispensing)' },
    { class: 'CLASS I (Low Risk)', label: 'Clergyman' },
    { class: 'CLASS I (Low Risk)', label: 'Engineer (Office-based)' },
    { class: 'CLASS I (Low Risk)', label: 'Photographer (Studio)' },
    { class: 'CLASS I (Low Risk)', label: 'School Teacher' },
    { class: 'CLASS I (Low Risk)', label: 'Insurance Officer' },
    { class: 'CLASS I (Low Risk)', label: 'Banker' },
    { class: 'CLASS II (Medium Risk)', label: 'Sales Representative' },
    { class: 'CLASS II (Medium Risk)', label: 'Buyer' },
    { class: 'CLASS II (Medium Risk)', label: 'Messenger' },
    { class: 'CLASS II (Medium Risk)', label: 'Farmer' },
    { class: 'CLASS II (Medium Risk)', label: 'Chemist' },
    { class: 'CLASS II (Medium Risk)', label: 'Inspector' },
    { class: 'CLASS II (Medium Risk)', label: 'Surveyor' },
    { class: 'CLASS II (Medium Risk)', label: 'Consulting Engineer' },
    { class: 'CLASS II (Medium Risk)', label: 'Tailor' },
    { class: 'CLASS II (Medium Risk)', label: 'Baker' },
    { class: 'CLASS II (Medium Risk)', label: 'Clerk (Work Site)' },
    { class: 'CLASS II (Medium Risk)', label: 'Confectioner' },
    { class: 'CLASS II (Medium Risk)', label: 'Draughtsman' },
    { class: 'CLASS II (Medium Risk)', label: 'Printer' },
    { class: 'CLASS II (Medium Risk)', label: 'Gardener' },
    { class: 'CLASS II (Medium Risk)', label: 'Shopkeeper' },
    { class: 'CLASS II (Medium Risk)', label: 'Musician' },
    { class: 'CLASS II (Medium Risk)', label: 'Sculptor' },
    { class: 'CLASS II (Medium Risk)', label: 'Tour Agent' },
    { class: 'CLASS III (High Risk)', label: 'Construction Worker' },
    { class: 'CLASS III (High Risk)', label: 'Site Engineer' },
    { class: 'CLASS III (High Risk)', label: 'Factory Worker' },
    { class: 'CLASS III (High Risk)', label: 'Butcher' },
    { class: 'CLASS III (High Risk)', label: 'Actor' },
    { class: 'CLASS III (High Risk)', label: 'Timber Merchant' },
    { class: 'CLASS III (High Risk)', label: 'Miner' },
    { class: 'CLASS III (High Risk)', label: 'Mechanic' },
    { class: 'CLASS III (High Risk)', label: 'Fisherman' },
    { class: 'CLASS III (High Risk)', label: 'Security Guard' },
    { class: 'CLASS III (High Risk)', label: 'Driver (Heavy/Commercial)' },
    { class: 'CLASS III (High Risk)', label: 'Labourer' },
  ];

  tierForm = this.formBuilder.group({
    selectedTier:  ['', Validators.required],
    occupation:    ['', Validators.required],
    coverStartDate: [moment(null), Validators.required],
    excelsior:  [false],
    passiveWar: [false],
    nextOfKin: this.formBuilder.array([this.buildNextOfKin()]),
  });

  todayDate: string = new Date().toISOString().split('T')[0];
  minCoverDate: Date = new Date();
  maxCoverDate: Date = new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate());

  relationships: string[] = [
    'Spouse', 'Parent', 'Child', 'Sibling', 'Guardian', 'Friend', 'Relative', 'Other',
  ];

  occupationList: string[] = [];
  filteredOccupations: string[] = [];
  private subs = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private titleService: Title,
    private esbPaService: EsbPaService,
  ) {
    this.occupationList = this.occupations
      .map(o => o.label)
      .concat(this.OTHER_OCCUPATION)
      .sort((a, b) => a.localeCompare(b));
    this.filteredOccupations = [...this.occupationList];
  }

  get isStudentCover(): boolean {
    return this.coverType === 'student';
  }

  // ── Next of kin ───────────────────────────────────────────────────────────

  buildNextOfKin(): FormGroup {
    return this.formBuilder.group({
      fullName:     ['', Validators.required],
      relationship: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      phoneNumber:  ['', [Validators.required, Validators.pattern(/^0[17]\d{8}$/)]],
      sharePercent: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
    });
  }

  get nextOfKin(): FormArray {
    return this.tierForm.get('nextOfKin') as FormArray;
  }

  addNextOfKin(): void {
    this.nextOfKin.push(this.buildNextOfKin());
  }

  removeNextOfKin(index: number): void {
    if (this.nextOfKin.length > 1) {
      this.nextOfKin.removeAt(index);
    }
  }

  get totalSharePercent(): number {
    return this.nextOfKin.controls.reduce(
      (sum, ctrl) => sum + (Number(ctrl.get('sharePercent')?.value) || 0), 0
    );
  }

  get sharePercentError(): boolean {
    return this.tierForm.touched && this.totalSharePercent !== 100;
  }

  ngOnInit(): void {
    this.titleService.setTitle('Personal Accident Quote | ICEA LION');

    // Occupation: filter autocomplete
    this.subs.add(
      this.tierForm.controls.occupation.valueChanges.subscribe(val => {
        const search = (val || '').toLowerCase();
        this.filteredOccupations = this.occupationList.filter(o => o.toLowerCase().includes(search));
      })
    );

    // Tier: re-fetch all premiums for the new tier
    this.subs.add(
      this.tierForm.controls.selectedTier.valueChanges.subscribe(() => {
        this.refreshAllPremiums();
      })
    );

    this.route.queryParams.subscribe((params) => {
      this.token      = params['token'] !== 'undefined' ? (params['token'] || '') : '';
      this.coverType  = params['coverType'] || '';
      this.isRenewal  = params['isRenewal'] === 'true';
      if (this.isStudentCover) {
        this.tierForm.controls.occupation.setValue('Student');
        this.tierForm.controls.selectedTier.setValue('A');
      }
      if (this.token) {
        // "I want to make a change" round trip — the customer already filled this form once
        // and reviewed a summary on WhatsApp, so prefill with what they submitted instead of
        // handing back a blank page. Runs after the student defaults above so it can
        // override them when there's a previously-submitted quote to restore.
        this.applyPrefill(this.token);
      }
    });
  }

  /**
   * Fetches the customer's previously-submitted PA quote details (keyed off the same
   * phone number the token embeds) and populates the form with them.
   */
  private async applyPrefill(token: string): Promise<void> {
    const prefillUrl = environment.baseUrl.includes('/motor')
      ? environment.baseUrl.replace('/motor', '/pa-breakout-prefill')
      : `${environment.baseUrl.replace(/\/$/, '')}/pa-breakout-prefill`;

    let prefill: any = null;
    try {
      const response = await fetch(`${prefillUrl}?token=${encodeURIComponent(token)}`);
      const data = await response.json();
      prefill = data && data.prefill;
    } catch {
      return;
    }
    if (!prefill || Object.keys(prefill).length === 0) return;

    // Occupation before tier — refreshAllPremiums (triggered by the selectedTier
    // valueChanges subscription below) reads occupation off the form when it runs.
    // A renewal prefill can carry a free-text occupation from the existing policy (e.g.
    // "SELF EMPLOYED") that isn't in our list — setting that would trip the "occupation not
    // covered" block and disable submit, so only prefill occupations we actually recognise.
    if (prefill.occupation) {
      const matched = this.occupationList.find(
        o => o.toLowerCase() === String(prefill.occupation).trim().toLowerCase()
      );
      if (matched) this.tierForm.controls.occupation.setValue(matched);
    }
    if (prefill.excelsior)  this.tierForm.controls.excelsior.setValue(true);
    if (prefill.passiveWar) this.tierForm.controls.passiveWar.setValue(true);
    if (prefill.coverStartDate) {
      const parsed = moment(prefill.coverStartDate);
      if (parsed.isValid()) this.tierForm.controls.coverStartDate.setValue(parsed);
    }
    if (prefill.selectedTierKey) this.tierForm.controls.selectedTier.setValue(prefill.selectedTierKey);

    if (Array.isArray(prefill.nextOfKin) && prefill.nextOfKin.length > 0) {
      while (this.nextOfKin.length > 0) this.nextOfKin.removeAt(0);
      prefill.nextOfKin.forEach((kin: any) => {
        const group = this.buildNextOfKin();
        // Beneficiaries parsed from an existing policy carry a relationship word ("Spouse",
        // "Child", ...) — only keep it if it's one the dropdown offers, otherwise leave blank
        // for the customer to pick rather than binding an option that won't render.
        const relationship = this.relationships.find(
          r => r.toLowerCase() === String(kin.relationship || '').trim().toLowerCase()
        ) || '';
        group.patchValue({
          fullName:     kin.fullName || '',
          relationship,
          emailAddress: kin.emailAddress || '',
          phoneNumber:  kin.phoneNumber || '',
          sharePercent: kin.sharePercent || '',
        });
        this.nextOfKin.push(group);
      });
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ── Pre-fetch all premium combinations ────────────────────────────────────

  /** Resolves the occupation class from the current form value, or null if unknown. */
  private getOccClass(): OccClass | null {
    if (this.isStudentCover) return 'Class I';
    const occLabel = (this.tierForm.controls.occupation.value || '').trim();
    const occ = this.occupations.find(o => o.label === occLabel);
    if (!occ) return null;
    return occ.class.startsWith('CLASS I (')  ? 'Class I'
         : occ.class.startsWith('CLASS II (') ? 'Class II'
         : 'Class III';
  }

  /**
   * Called whenever tier OR occupation changes.
   *
   * Requires BOTH tier and occupation class to be set — benefit limits and
   * premiums are both class-specific, so nothing meaningful can be shown
   * without knowing the user's class.
   *
   * When both are known, fires FOUR ESB calls in parallel (Promise.allSettled):
   *   1. Standard  warCoverage='N'  → benefit limits + base premium
   *   2. Excelsior warCoverage='N'  → excelsior limits + excelsior premium
   *   3. Standard  warCoverage='Y'  → war-inclusive standard premium
   *   4. Excelsior warCoverage='Y'  → war-inclusive excelsior premium
   *
   * All four results are stored so Excelsior and Passive War checkboxes can
   * show/hide pre-loaded values instantly — no extra ESB call on toggle.
   */

  private async refreshAllPremiums(): Promise<void> {
    const tier = this.tierForm.controls.selectedTier.value as BenOption;

    if (!tier) {
      this.clearAll();
      return;
    }

    if (this.isStudentCover) {
      this.isLoadingBenefit    = true;
      this.isLoadingExcelsior  = false;
      this.isLoadingPassiveWar = false;
      this.benefitLoadError    = null;
      this._std = {};
      this._exc = {};
      const fetchId = ++this._fetchId;
      try {
        const result = await this.esbPaService.fetchSingleStudent(tier);
        if (fetchId !== this._fetchId) return;
        this._std = {
          'Class I': {
            premium:        result.premium,
            premiumWithWar: result.premium,
            rawBenefits:    result.rawBenefits,
          },
        };
      } catch (err: any) {
        if (fetchId !== this._fetchId) return;
        this.benefitLoadError = 'Could not load student benefits. Please try again.';
      } finally {
        if (fetchId === this._fetchId) this.isLoadingBenefit = false;
      }
      return;
    }

    const fetchId = ++this._fetchId;

    this.isLoadingBenefit    = true;
    this.isLoadingExcelsior  = true;
    this.isLoadingPassiveWar = true;
    this.benefitLoadError    = null;
    this.excelsiorLoadError  = null;
    this.passiveWarLoadError = null;
    this._std = {};
    this._exc = {};

    console.log(
      `%c[PaTierBreakdown] ▶ Fetching all 6 class codes for Option=${tier}`,
      'color: #003087; font-weight: bold',
    );

    try {
      // 12 calls in parallel: Standard × 3 classes × warN+warY, Excelsior × 3 classes × warN+warY
      const [
        stdI_N, stdII_N, stdIII_N,
        excI_N, excII_N, excIII_N,
        stdI_Y, stdII_Y, stdIII_Y,
        excI_Y, excII_Y, excIII_Y,
      ] = await Promise.all([
        this.esbPaService.fetchSingleStandard(tier,  'Class I',   'N'),
        this.esbPaService.fetchSingleStandard(tier,  'Class II',  'N'),
        this.esbPaService.fetchSingleStandard(tier,  'Class III', 'N'),
        this.esbPaService.fetchSingleExcelsior(tier, 'Class I',   'N'),
        this.esbPaService.fetchSingleExcelsior(tier, 'Class II',  'N'),
        this.esbPaService.fetchSingleExcelsior(tier, 'Class III', 'N'),
        this.esbPaService.fetchSingleStandard(tier,  'Class I',   'Y'),
        this.esbPaService.fetchSingleStandard(tier,  'Class II',  'Y'),
        this.esbPaService.fetchSingleStandard(tier,  'Class III', 'Y'),
        this.esbPaService.fetchSingleExcelsior(tier, 'Class I',   'Y'),
        this.esbPaService.fetchSingleExcelsior(tier, 'Class II',  'Y'),
        this.esbPaService.fetchSingleExcelsior(tier, 'Class III', 'Y'),
      ]);

      if (fetchId !== this._fetchId) {
        console.log('[PaTierBreakdown] Batch results discarded (stale)');
        return;
      }

      this._std = {
        'Class I':   { premium: stdI_N.premium,   premiumWithWar: stdI_Y.premium,   rawBenefits: stdI_N.rawBenefits },
        'Class II':  { premium: stdII_N.premium,  premiumWithWar: stdII_Y.premium,  rawBenefits: stdII_N.rawBenefits },
        'Class III': { premium: stdIII_N.premium, premiumWithWar: stdIII_Y.premium, rawBenefits: stdIII_N.rawBenefits },
      };
      this._exc = {
        'Class I':   { premium: excI_N.premium,   premiumWithWar: excI_Y.premium,   rawBenefits: excI_N.rawBenefits },
        'Class II':  { premium: excII_N.premium,  premiumWithWar: excII_Y.premium,  rawBenefits: excII_N.rawBenefits },
        'Class III': { premium: excIII_N.premium, premiumWithWar: excIII_Y.premium, rawBenefits: excIII_N.rawBenefits },
      };

      console.log('%c[PaTierBreakdown] ✅ All class codes loaded', 'color: #27ae60; font-weight: bold', {
        tier,
        std: { I: stdI_N.premium, II: stdII_N.premium, III: stdIII_N.premium },
        exc: { I: excI_N.premium, II: excII_N.premium, III: excIII_N.premium },
      });

    } catch (err: any) {
      if (fetchId !== this._fetchId) return;
      console.error('[PaTierBreakdown] Failed to load premiums:', err);
      const msg = 'Unable to load cover rates. Please try again.';
      this.benefitLoadError    = msg;
      this.excelsiorLoadError  = msg;
      this.passiveWarLoadError = msg;
    } finally {
      if (fetchId === this._fetchId) {
        this.isLoadingBenefit    = false;
        this.isLoadingExcelsior  = false;
        this.isLoadingPassiveWar = false;
      }
    }
  }

  /** Clears all ESB-derived state without any spinner. */
  private clearAll(): void {
    this.isLoadingBenefit    = false;
    this.isLoadingExcelsior  = false;
    this.isLoadingPassiveWar = false;
    this.benefitLoadError    = null;
    this.excelsiorLoadError  = null;
    this.passiveWarLoadError = null;
    this._std = {};
    this._exc = {};
  }

  // ── Retry handler ──────────────────────────────────────────────────────────
  // All retry buttons (standard / excelsior / passive war) re-run the full
  // four-call batch — ESB is the single source of truth.

  private retryAll(): void {
    this.benefitLoadError    = null;
    this.excelsiorLoadError  = null;
    this.passiveWarLoadError = null;
    this.refreshAllPremiums();
  }

  retryLoadBenefits():          void { this.retryAll(); }
  retryLoadExcelsiorBenefits(): void { this.retryAll(); }
  retryLoadPassiveWarPremiums(): void { this.retryAll(); }

  // ── Tier helpers ───────────────────────────────────────────────────────────

  get tierEntries(): Array<{ key: BenOption; name: string }> {
    return (['A', 'B', 'C', 'D', 'E', 'F'] as BenOption[]).map(k => ({
      key:  k,
      name: TIER_NAMES[k],
    }));
  }

  get tierName(): string {
    const key = this.selectedTierKey as BenOption;
    return TIER_NAMES[key] || '';
  }

  /** Benefits to display — falls back to Class I as a preview when occupation is not yet selected. */
  get displayRawBenefits() {
    const c = this.getOccClass();
    return c ? (this._std[c]?.rawBenefits ?? []) : (this._std['Class I']?.rawBenefits ?? []);
  }

  /** True when we're showing Class I benefits as a preview (no occupation selected yet). */
  get isPreviewBenefits(): boolean {
    return !this.getOccClass() && this.displayRawBenefits.length > 0;
  }

  get hasBenefits(): boolean {
    return this.displayRawBenefits.length > 0;
  }

  get hasExcelsiorBenefits(): boolean {
    return this.currentExcelsiorRawBenefits.length > 0;
  }

  get selectedTierKey(): string {
    return this.tierForm.controls.selectedTier.value || '';
  }

  get isOtherOccupationSelected(): boolean {
    const rawValue        = this.tierForm.controls.occupation.value;
    const occupationValue = (rawValue || '').trim();
    if (!occupationValue) return false;
    if (occupationValue.toLowerCase() === this.OTHER_OCCUPATION.toLowerCase()) return true;
    return !this.occupations.some(
      o => o.label.toLowerCase() === occupationValue.toLowerCase()
    );
  }

  // ── Premium getters ────────────────────────────────────────────────────────
  //
  // Every getter checks the current checkbox values so the quote summary card
  // updates instantly when the user ticks or unticks Excelsior / Passive War
  // — no extra ESB call needed because all four combinations were pre-fetched.
  //
  // Display rules for the quote card:
  //
  //   Standard only          → Occupation Premium  |  Total Premium
  //   + Excelsior            → Occupation Premium  |  Excelsior Premium  |  Total Premium
  //   + Passive War only     → Occupation Premium  |  Passive War Premium  |  Total Premium
  //   + Both                 → Occupation Premium  |  Excelsior Premium  |  Subtotal  |  Passive War Premium  |  Total Premium
  //
  // "Subtotal" only appears when BOTH are on — it shows the pre-war sum so
  // the war component is visually separated from the rest.

  /** Standard annual premium (warN) for the current tier + class. */
  get annualPremium(): number | null {
    return this.currentPremium;
  }

  /**
   * Excelsior annual premium (warN).
   * Null when Excelsior is unchecked — row is hidden in the template.
   */
  get excelsiorAnnualPremium(): number | null {
    if (!this.tierForm.controls.excelsior.value) return null;
    return this.currentExcelsiorPremium;
  }

  /**
   * Pre-war subtotal — no longer applicable since excelsior replaces rather
   * than stacks on top of the occupational premium.
   */
  get subtotalPremium(): number | null {
    return null;
  }

  /**
   * Passive war premium component = warY_total − warN_total.
   * Sourced directly from ESB — not a 20% formula.
   * Null when Passive War is unchecked — row is hidden in the template.
   * When Excelsior is on the war component is derived from the excelsior
   * premiums only (excelsior replaces the occupational premium entirely).
   */
  get passiveWarPremium(): number | null {
    if (!this.tierForm.controls.passiveWar.value) return null;
    const isExcelsior = !!this.tierForm.controls.excelsior.value;
    if (isExcelsior) {
      if (this.currentExcelsiorPremiumWithWar === null || this.currentExcelsiorPremium === null) return null;
      return this.currentExcelsiorPremiumWithWar - this.currentExcelsiorPremium;
    }
    if (this.currentPremiumWithWar === null) return null;
    return this.currentPremiumWithWar - (this.currentPremium ?? 0);
  }

  /**
   * Final payable total — updates immediately on every checkbox change.
   *
   * Excelsior replaces (not stacks on) the occupational premium:
   *   Excelsior on,  Passive War on  → warY excelsior only
   *   Excelsior on,  Passive War off → warN excelsior only
   *   Excelsior off, Passive War on  → warY standard only
   *   Excelsior off, Passive War off → warN standard only
   */
  get totalPremium(): number | null {
    if (this.isStudentCover) {
      return this.currentPremium ?? null;
    }

    const isExcelsior  = !!this.tierForm.controls.excelsior.value;
    const isPassiveWar = !!this.tierForm.controls.passiveWar.value;

    if (isExcelsior) {
      if (this.currentExcelsiorPremium === null) return null;
      if (isPassiveWar) {
        if (this.currentExcelsiorPremiumWithWar === null) return null;
        return this.currentExcelsiorPremiumWithWar;
      }
      return this.currentExcelsiorPremium;
    }

    const std = this.annualPremium;
    if (std === null) return null;
    if (isPassiveWar) {
      if (this.currentPremiumWithWar === null) return null;
      return this.currentPremiumWithWar;
    }
    return std;
  }

  get selectedExcelsiorTier(): boolean {
    return this.hasExcelsiorBenefits;
  }

  toggleCoverBenefits(): void {
    this.coverBenefitsHidden = !this.coverBenefitsHidden;
  }

  toggleExcelsiorBenefits(): void {
    this.excelsiorBenefitsHidden = !this.excelsiorBenefitsHidden;
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  private async sendPayloadToRedirectUrl(payload: unknown): Promise<any> {
    const paRedirectUrl = environment.baseUrl.includes('/motor')
      ? environment.baseUrl.replace('/motor', '/pa-breakout')
      : `${environment.baseUrl.replace(/\/$/, '')}/pa-breakout`;

    const response = await fetch(paRedirectUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const rawText = await response.text();
    let parsedResponse: any = null;
    try {
      parsedResponse = rawText ? JSON.parse(rawText) : null;
    } catch {
      parsedResponse = { rawText };
    }
    return { ok: response.ok, status: response.status, data: parsedResponse };
  }

  private normalizeCoverStartDate(rawDate: Moment | null): string {
    if (!rawDate || !rawDate.isValid()) return '';
    return rawDate.format('YYYY-MM-DD');
  }

  async submitForm(): Promise<void> {
    if (
      this.tierForm.invalid          ||
      this.isOtherOccupationSelected ||
      this.isSubmitting              ||
      this.isLoadingBenefit          ||
      !this.hasBenefits              ||
      this.totalSharePercent !== 100
    ) {
      this.tierForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const selectedTierKey = this.selectedTierKey;
    const occupation      = (this.tierForm.controls.occupation.value || '').trim();
    const coverStartDateRaw = this.tierForm.controls.coverStartDate.value;
    const coverStartDate    = this.normalizeCoverStartDate(coverStartDateRaw);

    if (!coverStartDate || coverStartDate < this.todayDate) {
      this.tierForm.controls.coverStartDate.setErrors({ invalidCoverStartDate: true });
      this.tierForm.controls.coverStartDate.markAsTouched();
      this.isSubmitting = false;
      return;
    }

    const occupationClass = this.occupations.find(o => o.label === occupation)?.class || '';
    const isStudent       = this.isStudentCover;

    const firstBenefit = this.currentRawBenefits[0];
    const trainingLevy = firstBenefit?.TLevy   ?? null;
    const policyFund   = firstBenefit?.Pcf      ?? null;
    const stampDuty    = firstBenefit?.StampDuty ?? null;

    const payload = {
      product: isStudent ? 'STUDENT_PERSONAL_ACCIDENT' : 'PERSONAL_ACCIDENT',
      source:  'pa-tier-breakdown',
      filled:  true,
      coverType: this.coverType,
      token:     this.token,
      isRenewal: this.isRenewal,
      personalDetails: {
        CoverStartDate: coverStartDate,
      },
      selectedTier: {
        key:  selectedTierKey,
        name: this.tierName,
      },
      occupation: {
        value: occupation,
        class: occupationClass,
      },
      benefits: {
        excelsior:  isStudent ? false : !!this.tierForm.controls.excelsior.value,
        passiveWar: isStudent ? false : !!this.tierForm.controls.passiveWar.value,
      },
      excelsiorTierBenefits: isStudent ? null
        : (this.tierForm.controls.excelsior.value ? this.currentExcelsiorRawBenefits : null),
      benefitsList: this.tierForm.controls.excelsior?.value && !isStudent
        ? this.currentExcelsiorRawBenefits
        : this.currentRawBenefits,
      nextOfKin: this.tierForm.value.nextOfKin,
      benefitLabels: isStudent ? {} : {
        excelsior:  'Excelsior (includes Optical, Dental and Evacuations)',
        passiveWar: 'PVT coverage',
      },
      amounts: {
        occupationPremium: this.annualPremium,
        excelsiorPremium:  isStudent ? null : this.excelsiorAnnualPremium,
        subtotalPremium:   isStudent ? this.annualPremium : this.subtotalPremium,
        passiveWarPremium: isStudent ? null : this.passiveWarPremium,
        totalPremium:      this.totalPremium,
        ...(isStudent ? { trainingLevy, policyFund, stampDuty } : {}),
      },
      labels: {
        occupationPremium: 'Occupation Premium',
        ...(isStudent ? {} : {
          excelsiorPremium:  'Excelsior Premium',
          subtotalPremium:   'Subtotal Premium',
          passiveWarPremium: 'PVT Premium',
        }),
        totalPremium: 'Total Premium (Payable)',
      },
    };

    try {
      await this.sendPayloadToRedirectUrl(payload);
    } catch {
      this.isSubmitting = false;
    } finally {
      window.close();
    }
  }

  trackByTierKey(_index: number, tier: { key: string; name: string }): string {
    return tier.key;
  }
}
